import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, ChevronRight } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/auth/AuthContext";
import { getTeachingCourses, formatCourseLevels, type Course } from "@/api/lms/courses";
import { useMemo } from "react";

function courseLabel(course: Course) {
  const typeLabel = course.type === "LIVE" ? "Live" : "Recorded";
  return `${formatCourseLevels(course)} • ${typeLabel}`;
}

function CourseCard({ course }: { course: Course }) {
  return (
    <Link to={`/teacher/courses/${course.id}/structure`}>
      <KCard hoverable className="p-6 h-full flex flex-col justify-between">
        <div>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-bold text-foreground text-lg truncate">{course.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{courseLabel(course)}</p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs text-muted-foreground font-semibold">Open</div>
              <div className="text-sm font-bold text-primary">Structure</div>
            </div>
          </div>
        </div>

        <div className="mt-6 -mx-6 -mb-6 bg-muted/40 border-t border-border/60 px-6 py-3.5 rounded-b-xl flex items-center justify-between">
          <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
            <BookOpen size={14} className="text-primary/75" />
            Manage curriculum and lessons
          </span>
          <ChevronRight size={14} className="text-muted-foreground/75" />
        </div>
      </KCard>
    </Link>
  );
}

export default function TeacherCoursesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const coursesQuery = useQuery({
    queryKey: ["teachingCourses"],
    queryFn: getTeachingCourses,
  });

  const courses: Course[] = useMemo(() => coursesQuery.data ?? [], [coursesQuery.data]);

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="dash-page">
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">My Courses</h1>
            <p className="dash-page-subtitle">Courses assigned to you as a teacher.</p>
          </div>
          <Button onClick={() => navigate("/teacher")} variant="secondary" size="sm">
            Back to Dashboard
          </Button>
        </div>

        {coursesQuery.isPending ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        ) : courses.length === 0 ? (
          <div className="dash-empty-state">
            <BookOpen size={32} className="text-muted-foreground/30 mb-3" />
            <h3 className="text-sm font-medium mb-1">No assigned courses</h3>
            <p className="text-xs text-muted-foreground mb-6">
              Ask an admin to assign you as a teacher for one or more courses.
            </p>
            <Button asChild variant="default" size="sm">
              <Link to="/">Browse courses</Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {courses.map((c) => (
              <CourseCard key={c.id} course={c} />
            ))}
          </div>
        )}

        {coursesQuery.isError ? (
          <div className="mt-4 text-sm text-destructive">
            Failed to load your courses. {coursesQuery.error instanceof Error ? coursesQuery.error.message : ""}
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}

